import { useEffect, useState } from "react";
import { playChime, playOcean, stopOcean } from "../lib/chime";
import { useCompany } from "../context/CompanyContext";

const LOGO_CIRCLE = "/assets/luxstay-logo-circular.png";
const BG = "/assets/splash-santana.webp"; // Casas típicas de Santana — marca de água única

/**
 * Splash intro — aparece APENAS na primeira visita ao app (localStorage).
 * Background: uma única fotografia das casas típicas de Santana + overlay escuro + blur/saturação baixa.
 * Duração: 6 segundos.
 * Som: ambiente subtil de ondas do mar durante o splash + chime no fade-out.
 */
export default function SplashIntro({ onDone, duration = 6000 }) {
  const { company, logo } = useCompany();
  const [phase, setPhase] = useState("in"); // in → hold → out

  useEffect(() => {
    // Preload the single splash image
    const img = new Image();
    img.src = BG;
    // Start ocean waves ambient immediately (if autoplay allows)
    playOcean({ volume: 0.07 });
    const t1 = setTimeout(() => setPhase("hold"), 450);
    const t2 = setTimeout(() => {
      setPhase("out");
      playChime({ volume: 0.12 });
      stopOcean({ fadeMs: 600 });
    }, duration - 700);
    const t3 = setTimeout(() => {
      try {
        window.dispatchEvent(new Event("luxstay:splash-done"));
      } catch { /* ignore */ }
      onDone?.();
    }, duration);
    return () => {
      clearTimeout(t1); clearTimeout(t2); clearTimeout(t3);
      stopOcean({ fadeMs: 200 });
    };
  }, [duration, onDone]);

  const logoOpacity = phase === "in" ? 0 : phase === "hold" ? 1 : 0.85;
  const logoScale = phase === "in" ? 0.94 : phase === "out" ? 1.04 : 1;

  return (
    <div
      className="fixed inset-0 z-[100] grid place-items-center overflow-hidden bg-[#041f1b]"
      style={{ transition: "opacity 700ms ease", opacity: phase === "out" ? 0 : 1 }}
      data-testid="splash-intro"
    >
      {/* Single Madeira watermark — Santana houses with slow Ken Burns pan */}
      <div
        className="absolute inset-0 splash-bg-kb"
        style={{ backgroundImage: `url(${BG})` }}
        aria-hidden="true"
        data-testid="splash-bg"
      />

      {/* Dark vignette */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background: "radial-gradient(ellipse at center, rgba(4,31,27,0) 0%, rgba(4,31,27,0.45) 60%, rgba(4,31,27,0.8) 100%)",
      }} />
      {/* Grain */}
      <div className="absolute inset-0 pointer-events-none" style={{
        backgroundImage: "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.03) 1px, transparent 0)",
        backgroundSize: "3px 3px",
      }} />

      <div
        className="relative flex flex-col items-center px-6"
        style={{ transition: "opacity 1100ms ease, transform 1300ms ease", opacity: logoOpacity, transform: `scale(${logoScale})` }}
      >
        <img
          src={logo || LOGO_CIRCLE}
          alt={company.trade_name}
          className="w-[min(70vw,420px)] h-auto rounded-full drop-shadow-[0_0_80px_rgba(212,175,55,0.35)]"
          data-testid="splash-logo"
        />
        <div
          className="mt-10 text-[#d4af37] text-[0.7rem] md:text-xs"
          style={{
            letterSpacing: "0.45em",
            opacity: phase === "hold" ? 1 : 0,
            transition: "opacity 700ms ease 400ms",
          }}
          data-testid="splash-tagline"
        >
          GESTÃO DISCRETA · RESULTADO VISÍVEL
        </div>
        <div className="mt-6 w-52 h-[2px] bg-white/10 overflow-hidden">
          <div className="h-full bg-[#d4af37]" style={{
            animation: `splashBar ${duration - 500}ms cubic-bezier(.6,.08,.3,1) forwards`,
            transformOrigin: "left",
          }} />
        </div>
      </div>

      <style>{`
        @keyframes splashBar {
          0%   { transform: scaleX(0); }
          100% { transform: scaleX(1); }
        }
        @keyframes splashKenBurns {
          0%   { transform: scale(1.04) translate3d(-1%, -0.5%, 0); }
          100% { transform: scale(1.16) translate3d(1.5%, 1%, 0); }
        }
        .splash-bg-kb {
          background-size: cover;
          background-position: center;
          opacity: 0.32;
          filter: saturate(0.55) blur(2px) brightness(0.95);
          animation: splashKenBurns ${duration + 1000}ms ease-in-out forwards;
          will-change: transform;
        }
      `}</style>
    </div>
  );
}
